import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { URLS } from '../../../data/navigation/navigation.data';
import { findStoreListing, isFreeSku, storeAppListPrice, storeItemName } from '../../../data/product/product-lookup.data';
import { platformListPrice } from '../../../model/product/product.model';
import { CartPopupService, MiniCartHost } from '../../../service/cart/cart-popup.service';
import { CartItem, CartService } from '../../../service/cart/cart.service';
import { StoreCatalog, StoreService } from '../../../service/store/store.service';
import { UtilService } from '../../../service/util/util.service';
import { placeCaret } from '../../../util/popover-caret';
import { ClosableDropdown, DropdownCoordinatorService } from '../../dropdown-menu/dropdown-coordinator.service';

/** one line of the mini cart: the theme, the platform, the copies and what they cost */
export interface MiniCartRow {
  sku: string;
  logo: string;
  /** "Ocean Theme" */
  name: string;
  /** "for Sublime Text" */
  platform: string;
  quantity: number;
  priceLabel: string | null;
}

/** the mini cart opened by an Add to cart closes by itself, unless the pointer is on it */
const AUTO_CLOSE_MS = 6000;
/** ...and once the pointer leaves it again, a moment later */
const LEAVE_CLOSE_MS = 1500;
/** the panel the pointer opened closes this soon after it leaves, as Appearance's (owner 2026-10-17) */
const HOVER_CLOSE_MS = 220;

/**
 * The cart in the top nav: the bag with the count and the word "Cart", and
 * the panel that drops down from it (owner decision 2026-09-20; hovering
 * opens it like Appearance, 2026-10-17): the lines
 * in the cart, three at a time (more scroll), the subtotal and one button,
 * Go to cart. Click opens it; Add to cart anywhere on the site opens it
 * too, with the new line highlighted, and it closes by itself. Outside
 * click, Escape and leaving the page close it. It registers with the
 * dropdown coordinator, so it and the nav menus never overlap.
 */
@Component( {
  selector: 'app-mini-cart',
  templateUrl: './mini-cart.component.html',
  styleUrls: [ './mini-cart.component.scss' ],
} )
export class MiniCartComponent implements OnInit, OnDestroy, MiniCartHost, ClosableDropdown {

  URLS = URLS;

  open = false;
  /** the line an Add to cart just touched (highlighted while the panel is open for it) */
  highlightSku: string | null = null;
  count = 0;
  rows: MiniCartRow[] = [];
  subtotalLabel: string | null = null;

  private subs: Subscription[] = [];
  private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;
  /** opened by an Add to cart (closes by itself) rather than by a click */
  private autoOpened = false;
  /** the click that added the item is still on its way up to the document: it must not close what it opened */
  private openingClick = false;
  /** opened by the pointer resting on the bag: leaving closes it */
  private hoverOpened = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private router: Router,
    private cartService: CartService,
    private storeService: StoreService,
    private cartPopupService: CartPopupService,
    private coordinator: DropdownCoordinatorService,
    private utilService: UtilService,
  ) {
  }

  ngOnInit(): void {
    this.coordinator.register( this );
    this.cartPopupService.attach( this );
    this.subs.push(
      this.cartService.count$.subscribe( count => this.count = count ),
      // the lines show at once, priced from the listings' own list prices until the store answers
      combineLatest( [ this.cartService.items$, this.storeService.catalog$.pipe( startWith( { ok: false, items: [] } as StoreCatalog ) ) ] ).subscribe( ( [ items, catalog ] ) => {
        const bySku = this.storeService.indexBySku( catalog );
        this.rows = items.map( item => this.buildRow( item, bySku[ item.sku ]?.unitAmount ?? null, bySku[ item.sku ]?.currency, catalog.ok ) );
        this.subtotalLabel = this.computeSubtotal( items, bySku, catalog.ok );
      } ),
      this.router.events.pipe( filter( event => event instanceof NavigationStart ) ).subscribe( () => this.closeDropdown() ),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach( sub => sub.unsubscribe() );
    this.clearAutoClose();
    this.cartPopupService.detach( this );
    this.coordinator.unregister( this );
  }

  // ---------------------------------------------------------------- opening

  /** the bag in the nav: a click toggles the panel (a modified click keeps the link's own behaviour: a new tab) */
  toggle( event?: MouseEvent ): void {
    if ( event && ( event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0 ) ) return;
    event?.preventDefault();
    event?.stopPropagation();
    if ( this.open && !this.hoverOpened && !this.autoOpened ) {
      this.closeDropdown();
    } else {
      // a click on a panel the pointer (or an Add to cart) opened keeps it: it stays when the pointer leaves
      this.openPanel( false, null );
    }
  }

  /** the pointer rests on the bag or the panel: open it (hover-capable pointers only), keep one already open */
  hoverOpen(): void {
    this.holdOpen();
    if ( this.open || !this.utilService.supportsHover() ) return;
    this.openPanel( false, null );
    this.hoverOpened = true;
  }

  /** the pointer left the bag and the panel */
  hoverLeave(): void {
    if ( !this.open ) return;
    if ( this.hoverOpened ) this.scheduleClose( HOVER_CLOSE_MS );
    else if ( this.autoOpened ) this.scheduleClose( LEAVE_CLOSE_MS );
  }

  /** Add to cart, from anywhere: the panel opens with the line highlighted; false when the nav is not on screen */
  showAdded( sku: string ): boolean {
    if ( !this.isVisible() ) return false;
    this.openPanel( true, sku );
    return true;
  }

  /**
   * Go to cart / Browse themes: closes the panel, navigates, and lands at the
   * top of the page (owner ask 2026-09-20; the page scrolls on <body>, so this
   * sets it directly and once more after the new view has painted). A
   * modified click keeps the link's own behaviour (a new tab).
   */
  go( url: string, event?: MouseEvent ): void {
    if ( event && ( event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0 ) ) return;
    event?.preventDefault();
    this.closeDropdown();
    const toTop = () => {
      if ( typeof document === 'undefined' ) return;
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo( 0, 0 );
    };
    this.router.navigateByUrl( url ).then( () => {
      toTop();
      requestAnimationFrame( toTop );
      setTimeout( toTop, 250 );
    } );
  }

  /** ClosableDropdown: the coordinator closes this when a nav menu opens */
  closeDropdown(): void {
    this.clearAutoClose();
    this.open = false;
    this.autoOpened = false;
    this.hoverOpened = false;
    this.highlightSku = null;
  }

  /** the pointer is on the bag or the panel: it stays */
  holdOpen(): void {
    this.clearAutoClose();
  }

  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: Event ): void {
    if ( this.openingClick ) return;
    if ( this.open && !this.host.nativeElement.contains( event.target as Node ) ) this.closeDropdown();
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    if ( this.open ) this.closeDropdown();
  }

  trackBySku( index: number, row: MiniCartRow ): string {
    return row.sku;
  }

  // ---------------------------------------------------------------- helpers

  private openPanel( auto: boolean, sku: string | null ): void {
    this.coordinator.closeOthers( this );
    this.clearAutoClose();
    this.open = true;
    this.autoOpened = auto;
    this.hoverOpened = false;
    this.highlightSku = sku;
    setTimeout( () => {
      const element = this.host.nativeElement;
      placeCaret( element.querySelector( '.mini-cart-panel' ), element.querySelector( '.cart-label' ) );
    }, 0 );
    if ( auto ) {
      // the Add to cart click bubbles to the document after this; let it pass
      this.openingClick = true;
      setTimeout( () => this.openingClick = false, 0 );
      this.scheduleClose( AUTO_CLOSE_MS );
      // the line is at the end of a list that shows three at a time: bring it into view (owner ask 2026-09-20)
      setTimeout( () => this.revealHighlighted(), 0 );
    }
  }

  /** scrolls the list (not the page) so the highlighted line sits in the middle of the three */
  private revealHighlighted(): void {
    const list = this.host.nativeElement.querySelector( '.mini-cart-rows' ) as HTMLElement | null;
    const row = list?.querySelector( '.mini-cart-row.just-added' ) as HTMLElement | null;
    if ( !list || !row ) return;
    const top = row.offsetTop - list.offsetTop - Math.max( 0, ( list.clientHeight - row.offsetHeight ) / 2 );
    list.scrollTo( { top: Math.max( 0, top ), behavior: 'smooth' } );
  }

  private scheduleClose( ms: number ): void {
    this.clearAutoClose();
    this.autoCloseTimer = setTimeout( () => {
      this.autoCloseTimer = null;
      this.closeDropdown();
    }, ms );
  }

  private clearAutoClose(): void {
    if ( this.autoCloseTimer !== null ) {
      clearTimeout( this.autoCloseTimer );
      this.autoCloseTimer = null;
    }
  }

  /** the top nav (and so this) is hidden on small screens; a panel nobody can see must not open */
  private isVisible(): boolean {
    const element = this.host.nativeElement;
    return typeof window !== 'undefined' && !!element && element.offsetParent !== null && element.getClientRects().length > 0;
  }

  private buildRow( item: CartItem, unitAmount: number | null, currency: string | undefined, catalogOk: boolean ): MiniCartRow {
    const listing = findStoreListing( item.sku );
    const name = listing?.product?.name || listing?.productKey || item.sku;
    const listPrice = catalogOk ? undefined : ( platformListPrice( listing?.product ?? undefined, listing?.platformName ) ?? storeAppListPrice( item.sku ) );
    // a free listing (2026-09-21) is one copy and reads "Free"
    const free = unitAmount === 0 || isFreeSku( item.sku );
    const amount = free ? 0 : ( unitAmount ?? listPrice?.amount ?? null );
    return {
      sku: item.sku,
      logo: listing?.product?.logo || '',
      name: storeItemName( listing, name ),
      platform: listing?.platformName ? `for ${ listing.platformName }` : '',
      quantity: item.quantity,
      priceLabel: free ? 'Free' : ( amount === null ? null : this.storeService.formatPrice( amount * item.quantity, currency || listPrice?.currency || 'usd' ) ),
    };
  }

  /** the paid lines' total; "Free" when every line is free (2026-09-21) */
  private computeSubtotal( items: CartItem[], bySku: { [ sku: string ]: { unitAmount?: number | null; currency?: string } }, catalogOk: boolean ): string | null {
    if ( items.length === 0 ) return null;
    let total = 0;
    let currency = 'usd';
    let paidLines = 0;
    for ( const item of items ) {
      if ( bySku[ item.sku ]?.unitAmount === 0 || isFreeSku( item.sku ) ) continue;
      const listing = findStoreListing( item.sku );
      const listPrice = catalogOk ? undefined : ( platformListPrice( listing?.product ?? undefined, listing?.platformName ) ?? storeAppListPrice( item.sku ) );
      const amount = bySku[ item.sku ]?.unitAmount ?? listPrice?.amount ?? null;
      if ( amount === null || amount === undefined ) return null;
      total += amount * item.quantity;
      currency = bySku[ item.sku ]?.currency || listPrice?.currency || currency;
      paidLines += 1;
    }
    if ( paidLines === 0 ) return 'Free';
    return this.storeService.formatPrice( total, currency );
  }
}
